import React from "react";
import { TableContainer, Table, TableBody, TableHead, TableRow, TableCell, Grid } from "@mui/material";
import { EditableTableProps, WintFormKey } from "models/propTypes/wintFormPropsTypes";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";
import { UtilsHelper } from "utils/helpers";
import EditableTableRow from "./editableTableRow";
import { TableFooter } from "components/common/tables";

function EditableTable<T extends Record<string, any>>(props: EditableTableProps<T>) {
  const {
    connect,
    name,
    value,
    fields,
    isCompactTable,
    defaultRow,
    showRowIndexes,
    hideActions,
    allowPagination,
    hideActionsCell,
    showColumnHeaders,
  } = props;

  const maxRowsPerPage = 30;
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPage] = React.useState<number>(maxRowsPerPage);
  const pageStartIndex = page * rowsPerPage;
  const pageEndIndex = pageStartIndex + rowsPerPage;

  const onPageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const changeRowsPerPageHandler = (rowsPerPage: number) => {
    setPage(0);
    setRowsPage(rowsPerPage);
  };

  const updateRow = React.useCallback((index: number, newRow: T, shouldValidate?: boolean) => {
    connect(`${name}[${index}]` as WintFormKey<T>).setValue(newRow, shouldValidate);
  }, []);

  const removeRow = React.useCallback(
    (index: number) => {
      const connection = connect(name);
      const rows = [...(connection.value ?? [])];
      rows.splice(index, 1);
      connection.setValue(rows, true);
    },
    [connect]
  );

  const newRow = React.useMemo(
    () =>
      hideActions
        ? null
        : ({
            ...defaultRow,
            [props.idField!]: UtilsHelper.getUniqueId(),
          } as T),
    [defaultRow, props.idField, value.length, hideActions]
  );

  const rows = React.useMemo(() => (newRow ? [...(value ?? []), newRow] : [...(value ?? [])]), [value, newRow]);

  const getColumnHeaders = () => {
    if (!showColumnHeaders) return null;

    return (
      <TableHead>
        <TableRow className={cssExports.columnHeaderRow}>
          {showRowIndexes && <TableCell></TableCell>}
          {Object.keys(fields).map((key) => {
            const field = fields[key];
            // Use headerLabel if provided, otherwise fall back to label
            const headerLabel = field?.extraProps?.headerLabel || field?.extraProps?.label || "";
            const widthPercentage =
              field?.widthColumnCount && typeof field.widthColumnCount === "number"
                ? `${(field.widthColumnCount / 12) * 100}%`
                : undefined;

            return (
              <TableCell
                key={`header-${key}`}
                className={clsx(cssExports.tableCell, cssExports.blackHighlightText)}
                style={{ width: widthPercentage }}
              >
                {headerLabel}
              </TableCell>
            );
          })}
          {!hideActionsCell && <TableCell className={cssExports.editableTableActionButton}></TableCell>}
        </TableRow>
      </TableHead>
    );
  };

  return (
    <Grid
      container
      className={clsx(
        cssExports.tableListContainer,
        cssExports.editableTableContainer,
        isCompactTable ? cssExports.compactTable : "",
        props.className
      )}
      direction="column"
      data-testid={`${props.name}EditableTable`}
    >
      <TableContainer className={cssExports.tableContainerOuter} classes={{ root: cssExports.tableContainerRoot }}>
        <Table aria-label="simple table">
          {getColumnHeaders()}
          <TableBody className={cssExports.tableBody}>
            {(allowPagination ? rows.slice(pageStartIndex, pageEndIndex) : rows).map((row, rowIndex) => (
              <EditableTableRow<T>
                key={`table-row-${row[props.idField!] ?? rowIndex}`}
                connect={connect}
                name={name}
                index={rowIndex + pageStartIndex}
                row={row}
                fields={fields}
                idField={props.idField}
                updateRow={updateRow}
                removeRow={removeRow}
                isLastRow={rowIndex + pageStartIndex === rows.length - 1 && !!newRow}
                showRowIndex={showRowIndexes}
                hideActions={hideActions}
                hideActionsCell={hideActionsCell}
                data-testid={`${props.name}EditableTableRow${rowIndex}`}
              />
            ))}
          </TableBody>
        </Table>
        {allowPagination ? (
          <Grid container className={cssExports.tablePaginationContainer}>
            <TableFooter
              total={rows.length}
              rowsPerPage={rowsPerPage}
              maxRowsPerPage={maxRowsPerPage}
              currentPage={page}
              advancedPagination
              hideIfSinglePage
              changePageHandler={onPageChange}
              changeRowsPerPageHandler={changeRowsPerPageHandler}
            />
          </Grid>
        ) : (
          <></>
        )}
      </TableContainer>
    </Grid>
  );
}

export default EditableTable;
